import { useState, useEffect, useCallback, useRef } from "react";
import { TextField, InlineStack, Icon } from "@medentic/polaris-react";
import { CheckIcon, AlertTriangleIcon } from "@medentic/polaris-icons";
import { useTranslation } from "react-i18next";
import { validateUrl } from "@shared/lib";

interface WebsiteFieldProps {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  helpText?: string;
  disabled?: boolean;
  requiredIndicator?: boolean;
  placeholder?: string;
  prefix?: string;
  error?: string;
  requireHttps?: boolean;
}

type ValidationState = "idle" | "valid" | "invalid";

export default function WebsiteField({
  label,
  value,
  onChange,
  helpText,
  disabled = false,
  requiredIndicator = false,
  placeholder = "https://",
  prefix,
  error,
  requireHttps = true,
}: WebsiteFieldProps) {
  const { t } = useTranslation();
  const fieldValue = value || "";
  const [validationState, setValidationState] = useState<ValidationState>("idle");
  const [validationError, setValidationError] = useState<string | undefined>();
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Default label if not provided
  const effectiveLabel = label || t("common.website_field.label", { defaultValue: "Website" });
  
  // Default help text if not provided
  const baseHelpText = helpText || t("common.website_field.help_text", { defaultValue: "Enter a website URL" });

  // Enhance help text based on validation state
  const effectiveHelpText = (() => {
    if (validationState === "valid" && fieldValue.trim()) {
      return t("common.website_field.valid_url", { defaultValue: "Valid URL" });
    }
    return baseHelpText;
  })();

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Validate URL
  const validateUrlValue = useCallback(
    (urlValue: string) => {
      if (!urlValue.trim()) {
        setValidationState("idle");
        setValidationError(undefined);
        return;
      }

      const result = validateUrl(urlValue, {
        requireHttps,
        allowEmpty: false,
      });

      if (result.isValid) {
        setValidationState("valid");
        setValidationError(undefined);
      } else {
        setValidationState("invalid");
        // Translate error code to error message
        const errorMessage = result.errorCode 
          ? t(`common.website_field.${result.errorCode}`, { 
              defaultValue: t('common.website_field.invalid_url')
            })
          : t('common.website_field.invalid_url');
        setValidationError(errorMessage);
      }
    },
    [requireHttps, t]
  );

  // Handle value change with debouncing
  const handleValueChange = useCallback(
    (newValue: string) => {
      onChange(newValue);

      // Clear any existing timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Reset validation state when user starts typing
      if (validationState !== "idle") {
        setValidationState("idle");
        setValidationError(undefined);
      }

      // Set up new debounced validation
      if (newValue.trim()) {
        debounceTimerRef.current = setTimeout(() => {
          validateUrlValue(newValue);
        }, 300);
      } else {
        setValidationState("idle");
        setValidationError(undefined);
      }
    },
    [onChange, validationState, validateUrlValue]
  );

  // Determine display error (external error takes precedence)
  const displayError = error || validationError;

  // Determine suffix based on validation state
  const suffix =
    validationState === "valid" ? (
      <Icon source={CheckIcon} tone="success" />
    ) : validationState === "invalid" ? (
      <Icon source={AlertTriangleIcon} tone="critical" />
    ) : undefined;

  return (
    <InlineStack gap="200" blockAlign="center">
      <div style={{ flexGrow: 1 }}>
        <TextField
          label={effectiveLabel}
          value={fieldValue}
          onChange={handleValueChange}
          error={displayError}
          helpText={effectiveHelpText}
          disabled={disabled}
          requiredIndicator={requiredIndicator}
          placeholder={placeholder}
          prefix={prefix}
          suffix={suffix}
          autoComplete="url"
          type="url"
        />
        {/* Accessibility: Announce validation state changes to screen readers */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          style={{
            position: "absolute",
            width: "1px",
            height: "1px",
            padding: 0,
            margin: "-1px",
            overflow: "hidden",
            clip: "rect(0, 0, 0, 0)",
            whiteSpace: "nowrap",
            border: 0,
          }}
        >
          {validationState === "valid" && fieldValue && t("common.website_field.valid_url", { defaultValue: "Valid URL" })}
          {validationState === "invalid" && validationError}
        </div>
      </div>
    </InlineStack>
  );
}
